import * as React from "react";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import Typography from "@mui/material/Typography";
import { useNavigate } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import Avatar from "@mui/material/Avatar";
import Menu from "@mui/material/Menu";
import { useState } from "react";
import MenuItem from "@mui/material/MenuItem";
import { DEPLOY_ENV, IS_PRODUCTION } from "../../version.js";
import { useVersionCheck } from "../../useVersionCheck.js";
import VersionUpdateBanner from "../../components/VersionUpdateBanner.js";
import ViewAsBanner from "../../components/ViewAsBanner.js";
import { resetViewAs, useViewAs } from "../../viewAs.js";

export default function Header() {
  const navigate = useNavigate();
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const userMenuOpen = !!menuAnchorEl;
  const { deployedVersion, isStale } = useVersionCheck();
  const { readOnly, canViewAs } = useViewAs();

  const menuOpenClick = (e) => {
    setMenuAnchorEl(e.currentTarget);
  };
  const menuClose = () => setMenuAnchorEl(null);

  // The whole app bar changes colour off production, not just the label. This
  // app writes real patient and prescriber records, so the cost of acting on a
  // test tier believing it is production is high enough to be worth a tell that
  // is impossible to miss out of the corner of an eye.
  const accent = IS_PRODUCTION ? "primary" : "warning";

  return (
    <>
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          color: "white.main",
          backgroundImage: (theme) =>
            theme.functions.linearGradient(
              theme.palette.gradients[accent].main,
              theme.palette.gradients[accent].state,
              130
            ),
          backdropFilter: "blur(8px)",
          borderBottom: (theme) =>
            `1px solid ${theme.functions.rgba(theme.palette.black.main, 0.08)}`,
          boxShadow: (theme) =>
            `0 6px 20px -10px ${theme.functions.rgba(
              theme.palette[accent].main,
              0.6
            )}`,
        }}
      >
        {isStale && <VersionUpdateBanner deployedVersion={deployedVersion} />}
        <ViewAsBanner />
        <Toolbar sx={{ minHeight: 60 }}>
          <IconButton
            size="medium"
            edge="start"
            aria-label="back"
            onClick={() => navigate(-1)}
            sx={{
              mr: 1.5,
              color: "white.main",
              backgroundColor: (theme) =>
                theme.functions.rgba(theme.palette.white.main, 0.14),
              "&:hover": {
                backgroundColor: (theme) =>
                  theme.functions.rgba(theme.palette.white.main, 0.24),
              },
            }}
          >
            <ArrowBackIosNewIcon fontSize="small" />
          </IconButton>

          <Typography
            variant="h6"
            component="div"
            onClick={() => navigate("/dashboard")}
            sx={{
              flexGrow: 1,
              color: "white.main",
              fontWeight: 700,
              letterSpacing: "0.02em",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            WRS{" "}
            <Box
              component="span"
              sx={{ fontWeight: 300, opacity: 0.85, ml: 0.75 }}
            >
              Central
            </Box>
            {!IS_PRODUCTION && (
              <Box
                component="span"
                sx={{
                  ml: 1,
                  px: 0.75,
                  py: 0.125,
                  fontSize: "0.6rem",
                  fontWeight: 700,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  borderRadius: 0.75,
                  backgroundColor: (theme) =>
                    theme.functions.rgba(theme.palette.white.main, 0.25),
                }}
              >
                {DEPLOY_ENV}
              </Box>
            )}
          </Typography>

          <IconButton
            onClick={menuOpenClick}
            aria-label="account menu"
            sx={{ p: 0.25 }}
          >
            <Avatar
              sx={{
                width: 38,
                height: 38,
                bgcolor: (theme) =>
                  theme.functions.rgba(theme.palette.white.main, 0.2),
                color: "white.main",
                border: (theme) =>
                  `1.5px solid ${theme.functions.rgba(
                    theme.palette.white.main,
                    0.5
                  )}`,
              }}
            >
              <PersonIcon />
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>
      <Menu
        open={userMenuOpen}
        anchorEl={menuAnchorEl}
        onClose={menuClose}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {canViewAs && (
          <MenuItem onClick={resetViewAs}>
            {readOnly ? "Exit view-as" : "View as another user"}
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            navigate("/logout");
          }}
        >
          Logout
        </MenuItem>
      </Menu>
    </>
  );
}
